/**
 * The in-app assistant and the toolbar project (project scope card 6.3).
 * Kept out of AIChatPanel.js / useAiExtensionActions.js (megafiles: they get
 * a call site, not a feature).
 *
 * - Each turn names the active project in the user message's context block
 *   (not the system prompt: that one is static and cached).
 * - A board, or a top-level document / folder, the assistant creates while a
 *   project is active joins it — the same rule as creating one by hand
 *   (decision 0.2); a document also lands in the Documents space on screen.
 *   A nested document follows its parent, as in the sidebar.
 *
 * Nothing here widens what the assistant can reach: it gets the project's
 * NAME and id, which the user's own title bar shows, and the join is the
 * same write the UI makes.
 */
import { getProjectScope } from '../../scope/projectScopeStore';
import { addNewItemToActiveProject } from '../../scope/projectItems';
import { addNewDocToActiveProject } from '../../scope/docSpaceActions';

/** The project a new item would join, or null (All projects / No project / not resolved yet). */
export function activeProjectFor(scope = getProjectScope()) {
  return scope?.status === 'ready' && scope.activeProject ? scope.activeProject : null;
}

/** The context line appended to the user's message; '' without a project. */
export function projectContextLine(scope = getProjectScope()) {
  const project = activeProjectFor(scope);
  if (!project) return '';
  return `\n\n[Active project: ${JSON.stringify(String(project.name || 'Untitled project'))} (id: ${project.id}). Boards and top-level documents or folders you create join it automatically.]`;
}

const docWire = (scope) => (scope?.type === 'team' && scope.teamId != null ? `team:${scope.teamId}` : 'personal');

/**
 * After a create: puts the new item in the active project. `kind` is
 * 'board' | 'document' | 'folder'; `docScope` the store descriptor a document
 * was created in. Returns the project's name for the tool result, or null.
 * Never throws (the item exists either way).
 */
export async function joinActiveProjectAfterCreate(kind, id, docScope = null) {
  const project = activeProjectFor();
  if (!project || !id) return null;
  try {
    const added =
      kind === 'board'
        ? await addNewItemToActiveProject(`board:${id}`, 'personal')
        : await addNewDocToActiveProject(`${kind === 'folder' ? 'doc_folder' : 'document'}:${id}`, docWire(docScope));
    return added ? project.name || null : null;
  } catch (err) {
    console.warn('[project-scope] the assistant could not add its new item to the project:', err);
    return null;
  }
}
